import { DateTime } from "luxon";
import { Event } from "./types";
import { toFormat } from "@/lib/dateHelpers";
import { currencyFormatterWithPrefix } from "@/lib/currencyHelpers";
import { isActivityType } from "@/pages/activities/activity-types/_utils";

// Enum interfaces for CSV export
interface EnumOption {
  name: string;
  description: string;
}

interface RecruitmentEventOptionsData {
  purposeOptions?: {
    enumValues: EnumOption[];
  };
  recruitmentEventTypeOptions?: {
    enumValues: EnumOption[];
  };
}

/** Event type shape exposed after the activity type rollout. */
export interface EventWithType {
  eventType?: { id?: string; name?: string; source?: "activityType" | "legacyEventType" } | string;
  eventTypeSummary?: { id?: string; name?: string };
  activityType?:
    | {
        id?: string;
        title?: string;
        config?: {
          creditRates: {
            id: string;
            rateUnit: string;
            rateMultiplier: number;
            participationRole: string;
          }[];
        };
      }
    | string;
  activityTypeSummary?: { id?: string; title?: string };
}

function isFormEventType(
  eventType: EventWithType["eventType"]
): eventType is { id: string; name?: string; source: "activityType" | "legacyEventType" } {
  return (
    typeof eventType === "object" &&
    !!eventType?.id &&
    (eventType.source === "activityType" || eventType.source === "legacyEventType")
  );
}

export function getEventTypeName(event: EventWithType | undefined): string | undefined {
  if (!event) return undefined;
  return typeof event.activityType === "string"
    ? event.activityType
    : (event.activityType?.title ??
        event.activityTypeSummary?.title ??
        (typeof event.eventType === "string" ? event.eventType : event.eventType?.name) ??
        event.eventTypeSummary?.name);
}

export type EventKind = "pat" | "seminar" | "education" | "other";

export function getEventTypeId(event: EventWithType | undefined): string | undefined {
  if (!event) return undefined;
  if (typeof event.activityType !== "string" && event.activityType?.id) return event.activityType.id;
  if (event.activityTypeSummary?.id) return event.activityTypeSummary.id;
  return typeof event.eventType === "string"
    ? event.eventTypeSummary?.id
    : (event.eventType?.id ?? event.eventTypeSummary?.id);
}

export function getEventTypeForForm(event: EventWithType | undefined): {
  id: string;
  name: string;
  source?: "activityType" | "legacyEventType";
} {
  // Already-normalized form values must pass through. Re-deriving from
  // event.eventType alone would treat an activity-type id as legacyEventType.
  const existingFormEventType = event?.eventType;
  if (isFormEventType(existingFormEventType)) {
    return {
      id: existingFormEventType.id,
      name: existingFormEventType.name ?? "",
      source: existingFormEventType.source
    };
  }

  const id = getEventTypeId(event) ?? "";
  const name = getEventTypeName(event) ?? "";
  const source =
    (typeof event?.activityType !== "string" && event?.activityType?.id) || event?.activityTypeSummary?.id
      ? "activityType"
      : "legacyEventType";
  return { id, name, source };
}

export type RegistrationWindowSource = {
  registrationOpenAt?: string | null;
  registrationCloseAt?: string | null;
  timezone?: string | null;
};

export const isRegistrationClosed = (event?: RegistrationWindowSource): boolean => {
  if (!event?.registrationCloseAt) return false;

  const closesAt = event.timezone
    ? DateTime.fromISO(event.registrationCloseAt, { zone: event.timezone })
    : DateTime.fromISO(event.registrationCloseAt);

  return DateTime.now() > closesAt;
};

export const isRegistrationOpen = (event?: RegistrationWindowSource): boolean => {
  if (!event) {
    return false;
  }

  const now = DateTime.now();

  if (event.registrationOpenAt) {
    const opensAt = event.timezone
      ? DateTime.fromISO(event.registrationOpenAt, { zone: event.timezone })
      : DateTime.fromISO(event.registrationOpenAt);
    if (now < opensAt) {
      return false;
    }
  }

  if (event.registrationCloseAt) {
    const closesAt = event.timezone
      ? DateTime.fromISO(event.registrationCloseAt, { zone: event.timezone })
      : DateTime.fromISO(event.registrationCloseAt);
    if (now > closesAt) {
      return false;
    }
  }

  return true;
};

/**
 * Whether Cancel Registration can actually be performed right now.
 * For PAT events, staff cancel is only available after registration closes
 * so cancellations stay self-serve while registration is open.
 */
export const isCancelRegistrationPerformable = (event?: Event): boolean => {
  if (!isPAT(event)) return true;
  return isRegistrationClosed(event);
};

const isEducationActivityTypeTitle = (title: string | undefined): boolean => {
  return (
    isActivityType(title, "education-self-paced") ||
    isActivityType(title, "instructor-led") ||
    isActivityType(title, "education-self-reported")
  );
};

export const getLegacyEventTypeName = (event?: EventWithType): string | undefined => {
  return typeof event?.eventType === "string"
    ? event.eventType
    : (event?.eventType?.name ?? event?.eventTypeSummary?.name);
};

/** Match legacy event-type filter options against display and legacy names on a row. */
export function matchesEventTypeFilter(event: EventWithType | undefined, selectedEventTypeNames: string[]): boolean {
  if (selectedEventTypeNames.length === 0) return true;

  const displayName = getEventTypeName(event);
  const legacyName = getLegacyEventTypeName(event);

  return selectedEventTypeNames.some((selectedName) => selectedName === displayName || selectedName === legacyName);
}

const getActivityTypeTitle = (event?: EventWithType): string | undefined => {
  return typeof event?.activityType === "string"
    ? event.activityType
    : (event?.activityType?.title ?? event?.activityTypeSummary?.title);
};

export const getEventKind = (event?: EventWithType): EventKind => {
  const selectedTypeName = getEventTypeName(event);
  const legacyTypeName = getLegacyEventTypeName(event);
  const activityTypeTitle = getActivityTypeTitle(event);

  if (selectedTypeName === "PAT") return "pat";
  if ([legacyTypeName, activityTypeTitle].some((title) => title?.includes("Seminar"))) return "seminar";
  if (isEducationActivityTypeTitle(activityTypeTitle)) return "education";

  return "other";
};

export const isPAT = (event?: Event): boolean => {
  return getEventKind(event) === "pat";
};

export const isSeminar = (event?: EventWithType): boolean => {
  return getEventKind(event) === "seminar";
};

export const isEducationScopedEvent = (event?: EventWithType): boolean => {
  const kind = getEventKind(event);
  return kind === "seminar" || kind === "education";
};

// CSV Export functionality for recruitment events
export const RECRUITMENT_EVENT_CSV_HEADERS = [
  "Event Date",
  "Purpose",
  "Recruitment Event Type",
  "Event Name",
  "Facility",
  "Location",
  "Address",
  "Managing Section",
  "Total Attendance",
  "Total Leads",
  "Staff Attendees",
  "PGA Sponsorship Spend",
  "Expenses",
  "Event Rating"
];

export const generateRecruitmentEventCsvData = (
  eventData: Event,
  totalLeadsCount: number = 0,
  enumOptionsData?: RecruitmentEventOptionsData
): string[][] => {
  if (!eventData?.recruitmentEventAttributes) {
    return [];
  }

  const recruitment = eventData.recruitmentEventAttributes;

  // Format event date (MM/DD/YYYY)
  const eventDate = eventData.sessions?.[0]?.startsAt
    ? toFormat(eventData.sessions[0].startsAt, "MM/dd/yyyy") || ""
    : "";

  // Format address
  const addressParts = [eventData.address1, eventData.city, eventData.state, eventData.zip].filter(Boolean);
  const address = addressParts.join(", ");

  // Format staff attendees names
  const staffNames = recruitment.staffAttendees?.map((staff) => staff.displayName).join(",") || "";

  // Format sponsorship and expenses as currency (values come as cents)
  const sponsorshipSpend = recruitment.sponsorshipAsk
    ? currencyFormatterWithPrefix(recruitment.sponsorshipAsk.toString())
    : "";
  const expenses = recruitment.estimatedExpenses
    ? currencyFormatterWithPrefix(recruitment.estimatedExpenses.toString())
    : "";

  // Get human-readable descriptions for Purpose and Recruitment Event Type
  const purposeDescription =
    enumOptionsData?.purposeOptions?.enumValues?.find(
      (option) => option.name.toLowerCase() === recruitment.purpose?.toLowerCase()
    )?.description ||
    recruitment.purpose ||
    "";

  const recruitmentEventTypeDescription =
    enumOptionsData?.recruitmentEventTypeOptions?.enumValues?.find(
      (option) => option.name.toLowerCase() === recruitment.recruitmentEventType?.toLowerCase()
    )?.description ||
    recruitment.recruitmentEventType ||
    "";

  return [
    [
      eventDate,
      purposeDescription,
      recruitmentEventTypeDescription,
      eventData.title || "",
      eventData.facility?.name || "",
      eventData.location || "",
      address,
      eventData.section?.name || "",
      recruitment.totalAttendees?.toString() || "",
      totalLeadsCount.toString(),
      staffNames,
      sponsorshipSpend,
      expenses,
      recruitment.eventRating?.toString() || ""
    ]
  ];
};

/**
 * Downloads CSV data programmatically without requiring CSVLink component
 */
export const downloadCsv = (data: string[][], filename: string) => {
  // Convert array data to CSV string
  const csvContent = data
    .map((row) =>
      row
        .map((field) => {
          // Escape quotes and wrap fields containing commas, quotes, or newlines
          const needsQuotes = field.includes(",") || field.includes('"') || field.includes("\n");
          const escapedField = field.replace(/"/g, '""');
          return needsQuotes ? `"${escapedField}"` : escapedField;
        })
        .join(",")
    )
    .join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

export const downloadRecruitmentEventCsv = (
  eventData: Event,
  totalLeadsCount: number,
  eventSlug: string,
  enumOptionsData?: RecruitmentEventOptionsData
) => {
  const csvData = [
    RECRUITMENT_EVENT_CSV_HEADERS,
    ...generateRecruitmentEventCsvData(eventData, totalLeadsCount, enumOptionsData)
  ];

  const filename = `${eventSlug}-${Date.now()}.csv`;
  downloadCsv(csvData, filename);
};

export const isNationalMeeting = (event?: Event): boolean => {
  return getEventTypeName(event) === "National Meeting";
};
